/**
 * MentionPicker — autocomplete dropdown for @mentioning an assistant.
 * Shown by ChatInput when the user types `@` at a word boundary.
 *
 * - ArrowUp/Down navigate; Enter or Tab inserts; Escape closes without insert.
 * - Click an item to insert + close.
 * - Click outside closes without insert.
 * - Filter by case-insensitive prefix on id OR label.
 */

import { useEffect, useRef, useState } from "react";
import { COLORS, RADIUS, SPACE, TYPE } from "../theme/index.js";

export const ASSISTANTS = [
  { id: "jared", label: "Jared", description: "GTM & Sales" },
  { id: "russ", label: "Russ", description: "Pricing" },
  { id: "richard", label: "Richard", description: "Infrastructure" },
] as const;

export interface MentionPickerProps {
  query: string;
  onSelect: (slug: string) => void;
  onClose: () => void;
}

export function MentionPicker({ query, onSelect, onClose }: MentionPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(0);

  const q = query.toLowerCase();
  const filtered = ASSISTANTS.filter(
    (a) => a.id.toLowerCase().startsWith(q) || a.label.toLowerCase().startsWith(q),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: query drives highlight reset
  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  // Keyboard handler — listens at document level so it works while focus is in textarea
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (filtered.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((h) => (h + 1) % filtered.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((h) => (h - 1 + filtered.length) % filtered.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const choice = filtered[highlighted];
        if (choice) onSelect(choice.id);
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [filtered, highlighted, onSelect, onClose]);

  // Click-outside handler
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid="mention-picker"
      style={{
        position: "absolute",
        bottom: "100%",
        left: 0,
        right: 0,
        marginBottom: SPACE[1],
        background: COLORS.surface,
        border: `1px solid ${COLORS.borderHigh}`,
        borderRadius: RADIUS.lg,
        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        overflow: "hidden",
        zIndex: 10,
        maxWidth: 240,
      }}
    >
      {filtered.length === 0 && (
        <div
          style={{
            padding: `${SPACE[2]}px ${SPACE[3]}px`,
            color: COLORS.textDim,
            fontSize: TYPE.size.sm,
          }}
        >
          No agents found
        </div>
      )}
      {filtered.map((a, i) => (
        <button
          key={a.id}
          data-testid="mention-option"
          data-assistant={a.id}
          onMouseEnter={() => setHighlighted(i)}
          onClick={() => onSelect(a.id)}
          type="button"
          style={{
            width: "100%",
            padding: `${SPACE[2]}px ${SPACE[3]}px`,
            background: i === highlighted ? COLORS.brandSoft : "transparent",
            border: "none",
            borderBottom: i < filtered.length - 1 ? `1px solid ${COLORS.border}` : "none",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <span
            style={{
              fontSize: TYPE.size.sm,
              fontWeight: TYPE.weight.semibold,
              color: i === highlighted ? COLORS.brandText : COLORS.text,
            }}
          >
            {a.label}
          </span>
          <span
            style={{
              fontSize: TYPE.size.xs,
              color: COLORS.textDim,
            }}
          >
            {a.description}
          </span>
        </button>
      ))}
    </div>
  );
}
